import { BASE_PATH } from '@/constants/config';

/**
 * New app → legacy website for participant HTML routes when:
 * - The user is signed in as a non-researcher and their session does not have
 *   `v3Enabled: true` (core-api feature flag `participant-v3-release`, same evaluation as login).
 *
 * Paths match the rollout sheet (inverse of old → new). Query strings are passed through
 * via {@link appendSearch} / {@link serializeSearch} where noted.
 */

/** Logged-out visitors stay on the new app; researchers are never redirected by this rule. */
export function shouldRedirectParticipantSessionToLegacy(options: {
  user: { roles: string[] };
  v3Enabled: boolean | undefined;
}): boolean {
  if (options.user.roles.includes('researcher')) {
    return false;
  }
  return options.v3Enabled !== true;
}

export const LEGACY_HOME = '/respondents/v2/dashboard';

const MONGO_ID = /^[a-f0-9]{24}$/i;

/** New app path (no query) → legacy path. */
export const EXACT_PARTICIPANT_TO_LEGACY: Record<string, string> = {
  '/participants': LEGACY_HOME,
  '/participants/projects': LEGACY_HOME,
  '/participants/my-projects': '/respondents/v2/surveys',
  '/participants/messages': '/respondents/v2/messages',
  /** Participant login on legacy is `/respondents/v2/login`; `/login` is researcher (rollout sheet). */
  '/participants/login': '/respondents/v2/login',
  /** Rollout sheet: old `/create` → new signup. */
  '/participants/signup': '/signup?signupAs=participant',
  /** Not on rollout sheet; kept from prior `/respondents/v2/personal` → onboarding map. */
  '/participants/onboarding': '/respondents/v2/personal',
};

/** `tab` on `/participants/settings` → legacy path (rollout sheet). */
export const SETTINGS_TAB_TO_LEGACY: Record<string, string> = {
  referrals: '/respondents/v2/referrals',
  'profile-info': '/respondents/v2/settings',
  'account-settings': '/respondents/v2/settings',
  'social-accounts': '/respondents/v2/settings/social',
  /** Sheet: `/settings/notifications`, `/settings/cookie-settings` → this tab on new app. */
  /** Not explicitly on sheet; same hub as account settings. */
  'professional-background': '/respondents/v2/settings',
};

/** Longest / most specific first where relevant. */
const DYNAMIC_LEGACY: ReadonlyArray<{
  re: RegExp;
  legacyPath: (m: RegExpExecArray) => string;
}> = [
  {
    re: /^\/participants\/verify-work-email\/([^/]+)\/([^/]+)\/([^/]+)$/,
    legacyPath: (m) => {
      const userId = m[1];
      const token = m[3];
      if (!userId || !token) {
        return LEGACY_HOME;
      }
      return `/verify/${userId}/workemail/${token}`;
    },
  },
  {
    re: /^\/participants\/verify-email\/([^/]+)\/account\/([^/]+)$/,
    legacyPath: (m) => {
      const id = m[1];
      const token = m[2];
      if (!id || !token) {
        return LEGACY_HOME;
      }
      return `/verify/${id}/account/${token}`;
    },
  },
  {
    re: /^\/participants\/reset-password\/([^/]+)$/,
    legacyPath: (m) => {
      const token = m[1];
      if (!token) {
        return LEGACY_HOME;
      }
      return `/reset-password/${encodeURIComponent(token)}`;
    },
  },
  {
    // The booking pages need the screener response id on legacy, which the
    // new URL does not carry; the legacy projects list links to them.
    re: /^\/participants\/projects\/([^/]+)\/(schedule|reschedule|cancel-booking)$/,
    legacyPath: () => '/respondents/v2/surveys',
  },
  {
    re: /^\/participants\/projects\/([^/]+)\/(apply|interview)$/,
    legacyPath: (m) => {
      const id = m[1];
      if (!id || !MONGO_ID.test(id)) {
        return LEGACY_HOME;
      }
      return `/projects/view/${id}`;
    },
  },
  {
    re: /^\/participants\/projects\/([^/]+)\/([^/]+)$/,
    legacyPath: (m) => {
      const id = m[1];
      const slug = m[2];
      if (!id || !slug || !MONGO_ID.test(id)) {
        return LEGACY_HOME;
      }
      return `/projects/view/${id}/${slug}`;
    },
  },
  {
    re: /^\/participants\/projects\/([^/]+)$/,
    legacyPath: (m) => {
      const id = m[1];
      if (!id || !MONGO_ID.test(id)) {
        return LEGACY_HOME;
      }
      return `/projects/view/${id}`;
    },
  },
  {
    re: /^\/participants\/my-projects\/([^/]+)$/,
    legacyPath: (m) => {
      const id = m[1];
      if (!id || !MONGO_ID.test(id)) {
        return '/surveys';
      }
      return `/respondents/surveys/${id}/schedule`;
    },
  },
];

/**
 * Merges `requestSearch` into `legacyPath`. If `legacyPath` already contains a
 * `?…` query (e.g. `/signup/?signupAs=participant`), parameters are merged so we
 * do not produce a second `?` (which breaks parsing).
 */
export function appendSearch(
  legacyPath: string,
  requestSearch: string,
): string {
  const q = legacyPath.indexOf('?');
  const pathOnly = q === -1 ? legacyPath : legacyPath.slice(0, q);
  const legacyQuery = q === -1 ? '' : legacyPath.slice(q + 1);

  const merged = new URLSearchParams();
  if (legacyQuery.length > 0) {
    const fromLegacy = new URLSearchParams(legacyQuery);
    for (const [key, value] of fromLegacy) {
      merged.append(key, value);
    }
  }
  if (requestSearch.length > 0) {
    const fromRequest = parseSearch(requestSearch);
    for (const [key, value] of fromRequest) {
      merged.append(key, value);
    }
  }

  const serialized = merged.toString();
  return serialized.length > 0 ? `${pathOnly}?${serialized}` : pathOnly;
}

export function parseSearch(requestSearch: string): URLSearchParams {
  const raw = requestSearch.startsWith('?')
    ? requestSearch.slice(1)
    : requestSearch;
  return new URLSearchParams(raw);
}

export function serializeSearch(params: URLSearchParams): string {
  const s = params.toString();
  return s ? `?${s}` : '';
}

function stripNext(pathname: string): string {
  let p = pathname;
  for (;;) {
    if (p === BASE_PATH || p === `${BASE_PATH}/`) {
      return '/';
    }
    if (p.startsWith(`${BASE_PATH}/`)) {
      p = p.slice(BASE_PATH.length);
      continue;
    }
    break;
  }
  return p;
}

function trimSlash(p: string): string {
  if (p.length > 1 && p.endsWith('/')) {
    return p.slice(0, -1);
  }
  return p;
}

function pathAndQueryFromReturn(raw: string): { path: string; search: string } {
  const t = raw.trim();
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      return {
        path: trimSlash(stripNext(u.pathname)),
        search: u.search,
      };
    } catch {
      return { path: '/', search: '' };
    }
  }
  if (t.startsWith('//')) {
    try {
      const u = new URL(`https:${t}`);
      return {
        path: trimSlash(stripNext(u.pathname)),
        search: u.search,
      };
    } catch {
      return { path: '/', search: '' };
    }
  }
  const q = t.indexOf('?');
  if (q === -1) {
    return { path: trimSlash(stripNext(t)), search: '' };
  }
  return {
    path: trimSlash(stripNext(t.slice(0, q))),
    search: t.slice(q),
  };
}

export function mapNewAppReturnPathToLegacy(returnPath: string): string | null {
  const cleaned = [...returnPath]
    .filter((ch) => {
      const c = ch.codePointAt(0);
      return c !== undefined && c > 0x1f && c !== 0x7f;
    })
    .join('');
  const t = cleaned.trim();
  if (
    t.length > 0 &&
    !/^https?:\/\//i.test(t) &&
    !t.startsWith('//') &&
    !t.startsWith('/')
  ) {
    return null;
  }
  const { path, search } = pathAndQueryFromReturn(cleaned);
  return mapParticipantsPathToLegacyPathAndQuery(path, search);
}

export function mapParticipantsPathToLegacyPathAndQuery(
  pathname: string,
  search: string,
): string | null {
  const p = trimSlash(pathname);

  if (p === '/participants/sign-out') {
    return null;
  }

  if (p === '/participants/login' || p === '/participants/signup') {
    const params = parseSearch(search);
    const returnTo = params.get('returnTo');
    if (returnTo) {
      params.delete('returnTo');
      const mapped = mapNewAppReturnPathToLegacy(returnTo);
      const back = pathAndQueryFromReturn(returnTo);
      params.set('returnUrl', mapped ?? appendSearch(back.path, back.search));
    }
    const authLegacy = EXACT_PARTICIPANT_TO_LEGACY[p];
    if (!authLegacy) {
      return null;
    }
    return appendSearch(authLegacy, serializeSearch(params));
  }

  /** Legacy quantitative completion PATCH uses `cc` (projectCompletionId); forward only that param. */
  if (p === '/participants/complete') {
    const cc = parseSearch(search).get('cc');
    const legacyParams = new URLSearchParams();
    if (cc !== null) {
      legacyParams.set('cc', cc);
    }
    return appendSearch('/complete', serializeSearch(legacyParams));
  }

  /** Native inbox names the open-thread param `conversation`; legacy expects `conversationUid`. */
  if (p === '/participants/messages') {
    const params = parseSearch(search);
    const conversation = params.get('conversation');
    if (conversation !== null) {
      params.delete('conversation');
      if (conversation.length > 0) {
        params.set('conversationUid', conversation);
      }
    }
    return appendSearch('/respondents/v2/messages', serializeSearch(params));
  }

  const exact = EXACT_PARTICIPANT_TO_LEGACY[p];
  if (exact !== undefined) {
    return appendSearch(exact, search);
  }

  if (p === '/participants/settings') {
    const params = parseSearch(search);
    const tab = params.get('tab');
    params.delete('tab');
    const base = tab
      ? (SETTINGS_TAB_TO_LEGACY[tab] ?? '/settings')
      : '/settings';
    return appendSearch(base, serializeSearch(params));
  }

  for (const { re, legacyPath } of DYNAMIC_LEGACY) {
    const m = re.exec(p);
    if (m) {
      return appendSearch(legacyPath(m), search);
    }
  }

  if (p.startsWith('/participants/')) {
    return appendSearch(LEGACY_HOME, search);
  }

  return null;
}

export function buildLegacyParticipantRedirectUrl(options: {
  websiteUrl: string;
  pathname: string;
  search: string;
}): string | null {
  if (!options.websiteUrl) {
    return null;
  }
  const path = trimSlash(stripNext(options.pathname));
  if (!path.startsWith('/participants')) {
    return null;
  }
  const mapped = mapParticipantsPathToLegacyPathAndQuery(path, options.search);
  if (mapped == null) {
    return null;
  }
  const base = options.websiteUrl.replace(/\/$/, '');
  return `${base}${mapped}`;
}
